import { OptionType } from "@/api/admin";
import type { CurrencyOptionItem } from "@/api/admin/types";

import { useGetOptionsQuery } from "./useAdminHooks";

/**
 * Looks up one currency by its ISO code, so a screen can show the right symbol
 * and allow the right number of decimals.
 *
 * The currency list is the backend's, not ours — how many decimals JOD uses is
 * not something the portal should decide. Options are cached by react-query, so
 * several screens asking for this share one request.
 *
 * Returns null while loading, or when the code is unknown or missing. Callers
 * should fall back to showing nothing rather than guessing a symbol.
 */
export const useCurrencyQuery = (
  code: string | null | undefined
): CurrencyOptionItem | null => {
  const { data } = useGetOptionsQuery(OptionType.Currencies);

  if (!code) return null;

  return data?.currencies?.find(currency => currency.code === code) ?? null;
};

/** What `CurrencySymbol` needs to draw an amount. */
export interface CurrencySymbolProps {
  code?: string;
  symbol?: string;
}

/** The symbol, plus how many decimals the currency is written with. */
export interface CurrencyDisplayProps extends CurrencySymbolProps {
  decimals?: number;
}

/**
 * Same lookup, but for lists where each row may hold a different currency.
 *
 * Returns a plain function rather than a value, because a table renders its
 * cells in a loop and a hook cannot be called there. Fetch once at the top of
 * the component, then resolve per row.
 */
export const useCurrencyOptionsQuery = (): ((
  code: string | null | undefined
) => CurrencyDisplayProps) => {
  const { data } = useGetOptionsQuery(OptionType.Currencies);

  return (code: string | null | undefined): CurrencyDisplayProps => {
    if (!code) return {};

    const currency = data?.currencies?.find(item => item.code === code);
    // Fall back to the code itself ("JOD") rather than nothing, so an amount is
    // never left unlabelled while the options request is still in flight.
    return {
      code,
      symbol: currency?.symbol ?? code,
      decimals: currency?.decimals,
    };
  };
};
